/**
 * Keep the screen awake while a game is being scored.
 *
 * A scorekeeper sits open on the table between turns, so the phone locking
 * itself every 30 seconds is a real annoyance. The Screen Wake Lock API stops
 * that -- on iOS Safari 16.4+ and Android Chrome, over HTTPS.
 *
 * Two things make it fiddly, both handled here:
 *  - the browser releases the lock whenever the tab is hidden, so it has to be
 *    re-acquired when the page becomes visible again;
 *  - it is not supported everywhere, and a request can be refused, so every
 *    call is guarded and a failure is a silent no-op rather than an error.
 *
 * Framework-agnostic on purpose: the Vue lifecycle wrapper is a thin shell in
 * the component, this is the testable part.
 */

interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener(type: "release", listener: () => void): void;
}

interface WakeLockNavigator {
  wakeLock?: { request(type: "screen"): Promise<WakeLockSentinelLike> };
}

interface DocumentLike {
  visibilityState: DocumentVisibilityState;
  addEventListener(type: "visibilitychange", listener: () => void): void;
  removeEventListener(type: "visibilitychange", listener: () => void): void;
}

export interface WakeLock {
  /** Ask to keep the screen on, and keep it on across tab switches. */
  enable(): Promise<void>;
  /** Let the screen sleep again. */
  disable(): Promise<void>;
  /** Whether the platform offers the API at all. */
  readonly supported: boolean;
}

export function createWakeLock(nav: WakeLockNavigator, doc: DocumentLike): WakeLock {
  const supported = typeof nav.wakeLock?.request === "function";
  let sentinel: WakeLockSentinelLike | null = null;
  let wanted = false;

  async function acquire(): Promise<void> {
    // Only when we still want it, the page is visible, and we do not already
    // hold one. Requesting while hidden throws.
    if (!supported || !wanted || sentinel || doc.visibilityState !== "visible") return;
    try {
      sentinel = await nav.wakeLock!.request("screen");
      // The browser may drop it (low battery, tab hidden); forget it so the
      // next visibility change re-acquires rather than assuming we still hold it.
      sentinel.addEventListener("release", () => {
        sentinel = null;
      });
    } catch {
      // Refused or unavailable: leave the screen to its normal timeout.
      sentinel = null;
    }
  }

  async function release(): Promise<void> {
    const held = sentinel;
    sentinel = null;
    if (held) {
      try {
        await held.release();
      } catch {
        // Already gone; nothing to do.
      }
    }
  }

  const onVisibility = (): void => {
    if (doc.visibilityState === "visible") void acquire();
  };

  return {
    supported,
    async enable(): Promise<void> {
      if (!supported || wanted) return;
      wanted = true;
      doc.addEventListener("visibilitychange", onVisibility);
      await acquire();
    },
    async disable(): Promise<void> {
      if (!wanted) return;
      wanted = false;
      doc.removeEventListener("visibilitychange", onVisibility);
      await release();
    },
  };
}
